import { Controller } from '@hotwired/stimulus';
import { show, hide } from '@frontend/utils/dom';
import { dispatchCustomEvent } from '@frontend/utils/events';

export default class extends Controller {
  static targets = [
    'readingTimeFilterBar',
    'readingTimeCta',
    'list',
    'teaserListItem',
    'tenMinuteTeaserListItem',
    'twentyMinuteTeaserListItem',
    'loadMoreButton',
  ];
  static values = {
    maxReadingTimeInMinutes: {
      type: Number,
      default: 0, // Can be 0, 10 or 20
    },
    displayDefault: {
      type: Number,
      default: -1,
    },
    displayPerPage: {
      type: Number,
      default: -1,
    },
    currentPage: {
      type: Number,
      default: -1,
    },
    articleTeasersPerRow: {
      type: Number,
      default: 1,
    },
  };

  connect() {
    this.articleTeasersPerRowValue = window
      .getComputedStyle(this.listTarget)
      .getPropertyValue('--teaser-list-style-articles-per-row');

    if (
      this.tenMinuteTeaserListItemTargets.length === 0 ||
      this.twentyMinuteTeaserListItemTargets.length === 0
    ) {
      hide(this.readingTimeFilterBarTarget);
    }
  }

  resetVisibility() {
    this.teaserListItemTargets.forEach((element) => {
      hide(element);
      element.classList.remove('in-compact-row');
    });
    if (this.offsetForNextPage() === 0) {
      this.showNextPage();
    }
    this.currentPageValue = 1;
  }

  showNextPage() {
    const offset = this.offsetForNextPage();
    const availableItems = this.filteredTeaserListItems();
    const itemsToDisplay =
      this.currentPageValue === 1
        ? this.displayDefaultValue
        : this.displayPerPageValue;
    const visibleItems = availableItems.slice(offset, offset + itemsToDisplay);

    if (this.articleTeasersPerRowValue > 1) {
      this.divideArrayIntoGroups(
        visibleItems,
        this.articleTeasersPerRowValue
      ).forEach((row) => {
        const rowContainsCompact = row.some(
          (item) => item.dataset.isCompactFormat !== undefined
        );
        if (rowContainsCompact) {
          row.forEach((item) => item.classList.add('in-compact-row'));
        }
      });
    }
    visibleItems.forEach((element) => show(element));

    const remainingItems = availableItems.slice(offset + itemsToDisplay);
    if (remainingItems < 1) {
      this.handleNoMoreItems();
    }
  }

  divideArrayIntoGroups(originalArray, groupSize) {
    const dividedArray = [];
    for (let i = 0; i < originalArray.length; i += groupSize) {
      const group = originalArray.slice(i, i + groupSize);
      dividedArray.push(group);
    }
    return dividedArray;
  }

  offsetForNextPage() {
    if (this.currentPageValue === 1) return 0;

    return (
      this.displayDefaultValue +
      this.displayPerPageValue * (this.currentPageValue - 2)
    );
  }

  filteredTeaserListItems() {
    if (this.maxReadingTimeInMinutesValue === 0) {
      return this.teaserListItemTargets;
    } else if (this.maxReadingTimeInMinutesValue === 10) {
      return this.tenMinuteTeaserListItemTargets;
    } else if (this.maxReadingTimeInMinutesValue === 20) {
      return this.twentyMinuteTeaserListItemTargets;
    } else {
      throw 'Unexpected maxReadingTimeInMinutesValue';
    }
  }

  setReadingTimefilter({ params: { readingTime } }) {
    this.maxReadingTimeInMinutesValue = readingTime;
  }

  maxReadingTimeInMinutesValueChanged() {
    this.setCurrentReadingTimeFilterActive();
    this.resetVisibility();
  }

  setCurrentReadingTimeFilterActive() {
    this.readingTimeCtaTargets.forEach((target) =>
      target.classList.toggle(
        'is-active',
        target.dataset['articleTeaserIndex-ComponentReadingTimeParam'] ===
          this.maxReadingTimeInMinutesValue.toString()
      )
    );
  }

  currentPageValueChanged() {
    if (this.currentPageValue >= 0) {
      this.showNextPage();
    }
  }

  showMore() {
    this.currentPageValue += 1;

    if (this.offsetForNextPage() >= this.filteredTeaserListItems().length) {
      this.handleNoMoreItems();
    }
  }

  handleNoMoreItems() {
    hide(this.loadMoreButtonTarget);
    dispatchCustomEvent('article-teaser-index:no-more-items');
  }
}
